import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as S from './StatusDropdown.styled';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/dropdown-arrow.svg';

interface StatusOption {
    label: string;
    color: string;
    value?: string;
}

interface StatusDropdownProps {
    options: StatusOption[];
    initialValue?: string;
    onChange: (value: StatusOption) => void;
    disabled?: boolean;
}

const StatusDropdown = ({ initialValue, onChange, options, disabled }: StatusDropdownProps) => {
    const [selected, setSelected] = useState<StatusOption>(() => {
        return (
            options.find(
                (opt) => (opt.value && opt.value === initialValue) || opt.label === initialValue
            ) || options[0]
        );
    });
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleDropdown = () => {
        if (disabled) {
            return;
        }
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    const dropdown =
        open && position
            ? ReactDOM.createPortal(
                  <S.PortalDropdown
                      style={{ top: `${position.top}px`, left: `${position.left}px` }}
                      onMouseDown={(e) => e.stopPropagation()}
                  >
                      {options.map((status) => (
                          <S.DropdownItem
                              key={status.value}
                              onClick={() => {
                                  if (status.value !== 'VISA_RESULTS') {
                                      setSelected(status);
                                  }
                                  setOpen(false);
                                  onChange(status);
                              }}
                          >
                              <S.Dot style={{ backgroundColor: status.color }} />
                              <span>{status.label}</span>
                          </S.DropdownItem>
                      ))}
                  </S.PortalDropdown>,
                  document.body
              )
            : null;

    return (
        <S.Container>
            <S.Button
                ref={buttonRef}
                onClick={handleToggleDropdown}
                color={selected.color}
                title={selected.label}
                type="button"
                disabled={disabled}
            >
                <S.Label>{selected.label}</S.Label>
                <ArrowDownIcon
                    fill={'#282828'}
                    style={{
                        width: '12px',
                        height: '12px',
                        transform: open ? 'rotate(180deg)' : 'rotate(0)',
                        transition: '0.2s ease',
                    }}
                />
            </S.Button>
            {dropdown}
        </S.Container>
    );
};

export default StatusDropdown;
